/**
 * SelectionCounter Component
 *
 * Displays the number of currently selected components with quick actions.
 *
 * Features:
 * - Shows selection count with smooth animations
 * - Quick action buttons (Select All, Clear Selection, Delete)
 * - Auto-hides when no selection
 * - Keyboard shortcut hints
 * - WCAG 2.1 AA compliant
 *
 * Week 3: Selection System implementation
 */

import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { useEditorStore } from '@/stores/editor/editor-store';
import { Button } from '@/components/ui/button';
import { X, Trash2, CheckSquare, Eye } from 'lucide-react';
import { toast } from 'sonner';
import {
  FOREST_GREEN,
  CHARCOAL,
  MID_GRAY,
  WHITE,
} from '@/styles/tokens';
import { getModalConfig, ModalPreviewOverlay } from '@/components/shared/rendering/ContainerModalWrapper';
import { SharedComponentRenderer } from '@/components/shared/rendering/SharedComponentRenderer';
import { Component, Viewport } from '@/types';

interface SelectionCounterProps {
  /**
   * Position on screen
   * Default: 'bottom-left'
   */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

  /**
   * Show quick action buttons
   * Default: true
   */
  showActions?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Callback fired when actions are triggered
   */
  onAction?: (action: 'clear' | 'delete' | 'select-all') => void;
}

/**
 * SelectionCounter Component
 *
 * @example
 * ```tsx
 * function EditorLayout() {
 *   return (
 *     <div className="relative">
 *       <SelectionCounter
 *         position="bottom-left"
 *         showActions={true}
 *         onAction={(action) => {
 *           console.log('Action triggered:', action);
 *         }}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export const SelectionCounter: React.FC<SelectionCounterProps> = ({
  position = 'bottom-left',
  showActions = true,
  className,
  onAction,
}) => {
  const {
    selectedComponents,
    components,
    clearSelection,
    deleteSelectedComponents,
    selectAllComponents,
    currentPageId,
    pageComponents,
  } = useEditorStore();

  const [previewingModalId, setPreviewingModalId] = useState<string | null>(null);

  const selectedCount = selectedComponents.length;
  const totalCount = components.filter(c => !c.hidden && !c.isDeleted).length;

  // Find modal ancestor for selected component(s)
  const allPageComponents = useMemo(() => {
    if (!currentPageId) return [];
    return pageComponents.get(currentPageId) || [];
  }, [currentPageId, pageComponents]);

  const modalAncestor = useMemo(() => {
    if (selectedCount !== 1) return null;
    const selectedId = selectedComponents[0];
    // Check if selected component itself is a modal
    const selected = allPageComponents.find(c => c.id === selectedId);
    if (!selected) return null;
    if (getModalConfig(selected)) return selected;
    // Walk up parent chain
    let parentId = selected.parentId;
    while (parentId) {
      const parent = allPageComponents.find(c => c.id === parentId);
      if (!parent) break;
      if (getModalConfig(parent)) return parent;
      parentId = parent.parentId;
    }
    return null;
  }, [selectedCount, selectedComponents, allPageComponents]);

  const modalAncestorConfig = modalAncestor ? getModalConfig(modalAncestor) : null;

  // Don't render if no selection
  if (selectedCount === 0) {
    return null;
  }

  const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
  };

  const handleClear = () => {
    clearSelection();
    onAction?.('clear');
  };

  const handleDelete = () => {
    const count = selectedCount;
    deleteSelectedComponents();
    toast.success(`Deleted ${count} component${count === 1 ? '' : 's'}`);
    onAction?.('delete');
  };

  const handleSelectAll = () => {
    selectAllComponents();
    toast.success(`Selected all ${totalCount} components`);
    onAction?.('select-all');
  };

  return (
    <div
      className={cn(
        'fixed z-50',
        'group', // Enable group-hover for keyboard hints
        'flex items-center gap-2',
        'px-4 py-2 rounded-lg',
        'bg-background/95 backdrop-blur-sm',
        'border border-border shadow-lg',
        'animate-in fade-in-0 slide-in-from-bottom-2 duration-300',
        positionClasses[position],
        className
      )}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={`${selectedCount} component${selectedCount === 1 ? '' : 's'} selected out of ${totalCount} total`}
      data-testid="selection-counter"
    >
      {/* Selection count */}
      <div className="flex items-center gap-2">
        <div
          className="flex items-center justify-center w-8 h-8 rounded-full"
          style={{ backgroundColor: `${FOREST_GREEN.DEFAULT}1A` }}
        >
          <span className="text-sm font-bold" style={{ color: FOREST_GREEN.DEFAULT }}>
            {selectedCount}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-semibold">
            {selectedCount === 1 ? 'component' : 'components'} selected
          </span>
          {totalCount > 0 && (
            <span className="text-xs" style={{ color: MID_GRAY.DEFAULT }}>
              {selectedCount} of {totalCount} total
            </span>
          )}
        </div>
      </div>

      {/* Quick actions */}
      {showActions && (
        <div className="flex items-center gap-1 ml-2 pl-2 border-l border-border">
          {/* Modal Preview (when selected component is inside a modal) */}
          {modalAncestor && modalAncestorConfig && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2"
              onClick={() => setPreviewingModalId(modalAncestor.id)}
              title="Preview modal"
              aria-label={`Preview modal: ${modalAncestorConfig.title}`}
            >
              <Eye className="w-4 h-4" />
            </Button>
          )}

          {/* Select All */}
          {selectedCount < totalCount && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2"
              onClick={handleSelectAll}
              title="Select all components (Ctrl/Cmd+A)"
              aria-label={`Select all ${totalCount} components. Keyboard shortcut: Control or Command plus A`}
            >
              <CheckSquare className="w-4 h-4" />
            </Button>
          )}

          {/* Clear Selection */}
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2"
            onClick={handleClear}
            title="Clear selection (Esc)"
            aria-label={`Clear selection of ${selectedCount} component${selectedCount === 1 ? '' : 's'}. Keyboard shortcut: Escape`}
          >
            <X className="w-4 h-4" />
          </Button>

          {/* Delete */}
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleDelete}
            title="Delete selected (Delete/Backspace)"
            aria-label={`Delete ${selectedCount} selected component${selectedCount === 1 ? '' : 's'}. Keyboard shortcut: Delete or Backspace`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Modal Preview Overlay */}
      {previewingModalId && modalAncestor && modalAncestorConfig && (
        <ModalPreviewOverlay
          modalConfig={modalAncestorConfig}
          componentId={modalAncestor.id}
          onClose={() => setPreviewingModalId(null)}
        >
          <SelectionModalPreviewContent
            component={modalAncestor}
            allComponents={allPageComponents}
          />
        </ModalPreviewOverlay>
      )}

      {/* Keyboard shortcut hints (only visible on hover) */}
      <div
        className={cn(
          'absolute bottom-full left-0 mb-2',
          'px-3 py-2 rounded-md',
          'text-xs',
          'whitespace-nowrap',
          'opacity-0 group-hover:opacity-100',
          'transition-opacity duration-200',
          'pointer-events-none'
        )}
        style={{
          backgroundColor: CHARCOAL.DEFAULT,
          color: WHITE,
        }}
      >
        <div className="space-y-1">
          <div><kbd className="px-1 py-0.5 rounded" style={{ backgroundColor: CHARCOAL.light }}>Esc</kbd> Clear</div>
          <div><kbd className="px-1 py-0.5 rounded" style={{ backgroundColor: CHARCOAL.light }}>Del</kbd> Delete</div>
          <div><kbd className="px-1 py-0.5 rounded" style={{ backgroundColor: CHARCOAL.light }}>⌘A</kbd> Select All</div>
        </div>
      </div>
    </div>
  );
};

/** Renders modal children for the selection counter preview overlay */
const SelectionModalPreviewContent: React.FC<{
  component: Component;
  allComponents: Component[];
}> = ({ component, allComponents }) => {
  const childComponents = useMemo(() => {
    const enhanced = (component as any)._childComponents;
    if (enhanced && enhanced.length > 0) return enhanced;
    if (component.children?.length > 0) {
      return component.children
        .map((childId: string) => allComponents.find((c) => c.id === childId))
        .filter(Boolean) as Component[];
    }
    return allComponents.filter((c) => c.parentId === component.id);
  }, [component, allComponents]);

  if (childComponents.length === 0) {
    return (
      <div className="text-sm text-muted-foreground text-center py-8">
        No form fields in this modal yet
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {childComponents.map((child: Component) => (
        <SharedComponentRenderer
          key={child.id}
          component={child}
          allComponents={allComponents}
          viewport={Viewport.DESKTOP}
        />
      ))}
    </div>
  );
};

/**
 * Memoized version for performance optimization
 */
export const MemoizedSelectionCounter = React.memo(SelectionCounter);
